import React, { useState, useMemo } from 'react';
import styled, { useTheme } from 'styled-components';
import { tokens } from '../../utils/theme';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    ReferenceLine,
    Legend
} from 'recharts';

const Container = styled.div`
    background: ${props => props.theme.palette.mode === 'dark'
        ? 'radial-gradient(circle at top left, rgba(130, 170, 255, 0.05), rgba(12, 14, 23, 0.95))'
        : 'radial-gradient(circle at top left, rgba(15,118,110,0.04), rgba(255,255,255,0.95))'};
    border: 1px solid ${props => tokens(props.theme.palette.mode).grey[500]};
    border-radius: 14px;
    padding: 20px 20px 0 20px;
    color: ${props => props.theme.palette.text.primary};
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
`;

const ScrollableContent = styled.div`
    flex: 1;
    overflow-y: auto;
    padding-right: 10px;
    padding-bottom: 24px;
    margin-right: -10px;

    &::-webkit-scrollbar {
        width: 6px;
        height: 6px;
    }
    &::-webkit-scrollbar-track {
        background: rgba(0, 0, 0, 0.22);
        border-radius: 999px;
        border: 1px solid rgba(255, 255, 255, 0.08);
    }
    &::-webkit-scrollbar-thumb {
        background: linear-gradient(180deg, rgba(128, 203, 196, 0.26), rgba(128, 203, 196, 0.44));
        border-radius: 999px;
        border: 1px solid rgba(128, 203, 196, 0.20);
    }
    &::-webkit-scrollbar-thumb:hover {
        background: linear-gradient(180deg, rgba(128, 203, 196, 0.4), rgba(128, 203, 196, 0.6));
    }
`;

const Stack = styled.div`
    display: flex;
    flex-direction: column;
    gap: 14px;
`;

const SectionTitle = styled.h3`
    margin: 0;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${props => props.theme.palette.mode === 'dark' ? '#e6edf7' : '#0b1220'};
`;

const MeetingsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 10px;

    @media (max-width: 1000px) {
        grid-template-columns: repeat(2, 1fr);
    }
    @media (max-width: 600px) {
        grid-template-columns: 1fr;
    }
`;

const MeetingCard = styled.div`
    background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.22)' : 'rgba(255, 255, 255, 0.6)'};
    border: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(15, 23, 42, 0.06)'};
    border-radius: 8px;
    padding: 10px 14px;
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const MeetingHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

const MeetingBank = styled.span`
    font-size: 13.5px;
    font-weight: 500;
    color: ${props => props.theme.palette.mode === 'dark' ? '#e6edf7' : '#0b1220'};
`;

const MeetingBadge = styled.span`
    padding: 1px 6px;
    border-radius: 3px;
    font-size: 10px;
    font-weight: 700;
    background: ${props => props.$urgent ? 'rgba(239, 83, 80, 0.15)' : 'rgba(128, 203, 196, 0.15)'};
    color: ${props => props.$urgent ? '#ef5350' : '#80cbc4'};
`;

const MeetingDate = styled.div`
    font-size: 13px;
    font-weight: 400;
    font-style: italic;
    color: ${props => props.theme.palette.mode === 'dark' ? '#e6edf7' : '#0b1220'};
`;

const MeetingSub = styled.span`
    font-size: 11px;
    color: ${props => tokens(props.theme.palette.mode).grey[400]};
`;

const Card = styled.div`
    background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.22)' : 'rgba(255, 255, 255, 0.6)'};
    border: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(15, 23, 42, 0.06)'};
    border-radius: 8px;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const CardHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
`;

const ControlsGroup = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
`;

const ToggleBtn = styled.button`
    background: ${props => props.$active
        ? (props.theme.palette.mode === 'dark' ? 'rgba(128, 203, 196, 0.18)' : 'rgba(15, 118, 110, 0.14)')
        : 'transparent'};
    border: 1px solid ${props => props.$active
        ? '#80cbc4'
        : (props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(15, 23, 42, 0.12)')};
    color: ${props => props.$active
        ? '#80cbc4'
        : tokens(props.theme.palette.mode).grey[300]};
    font-size: 11.5px;
    font-weight: 600;
    padding: 5px 10px;
    min-height: 27px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.12s ease;

    &:hover {
        border-color: #80cbc4;
        color: #fff;
    }
`;

const ChipsWrap = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 6px;
`;

const BankChip = styled.button`
    background: ${props => props.$selected
        ? (props.theme.palette.mode === 'dark' ? 'rgba(128, 203, 196, 0.18)' : 'rgba(15, 118, 110, 0.14)')
        : (props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(15, 23, 42, 0.03)')};
    border: 1px solid ${props => props.$selected
        ? '#80cbc4'
        : (props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(15, 23, 42, 0.1)')};
    color: ${props => props.$selected
        ? '#80cbc4'
        : tokens(props.theme.palette.mode).grey[300]};
    font-size: 12px;
    font-weight: 600;
    padding: 6px 12px;
    min-height: 29px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.12s ease;

    &:hover {
        border-color: #80cbc4;
        color: #fff;
    }
`;

const TableWrapper = styled.div`
    overflow-x: auto;
    border-radius: 6px;
    border: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(15, 23, 42, 0.08)'};
`;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
`;

const Th = styled.th`
    background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(15, 23, 42, 0.03)'};
    color: ${props => tokens(props.theme.palette.mode).grey[300]};
    font-size: 10.5px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    text-align: ${props => props.$align || 'left'};
    padding: 9px 12px;
    border-bottom: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(15, 23, 42, 0.08)'};
    white-space: nowrap;
`;

const Td = styled.td`
    padding: 9px 12px;
    border-bottom: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : 'rgba(15, 23, 42, 0.04)'};
    text-align: ${props => props.$align || 'left'};
    color: ${props => props.$color || (props.theme.palette.mode === 'dark' ? '#e6edf7' : '#0b1220')};
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
`;

const Tr = styled.tr`
    transition: background 0.12s ease;

    &:hover {
        background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(128, 203, 196, 0.06)' : 'rgba(15, 118, 110, 0.04)'};
    }
`;

const StanceBadge = styled.span`
    padding: 2px 7px;
    border-radius: 3px;
    font-size: 11px;
    font-weight: 600;
    background: ${props => props.$bg || 'rgba(128, 203, 196, 0.12)'};
    color: ${props => props.$color || '#80cbc4'};
`;

const BpBadge = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    font-weight: 700;
    padding: 1px 5px;
    border-radius: 3px;
    margin-left: 6px;
    font-variant-numeric: tabular-nums;
    background: ${props => props.$isCut
        ? 'rgba(239, 83, 80, 0.12)'
        : (props.$isHike ? 'rgba(38, 166, 154, 0.12)' : 'rgba(255, 255, 255, 0.08)')};
    color: ${props => props.$isCut
        ? '#ef5350'
        : (props.$isHike ? '#80cbc4' : props.theme.palette.text.secondary)};
    border: 1px solid ${props => props.$isCut
        ? 'rgba(239, 83, 80, 0.28)'
        : (props.$isHike ? 'rgba(38, 166, 154, 0.28)' : 'rgba(255, 255, 255, 0.12)')};
`;

const G10_KEYS = ['FED', 'ECB', 'BOE', 'SNB', 'RBA', 'BOC', 'BOJ'];

// Simple harmonious colors for timeline comparison
const TIMELINE_COLORS = {
    FED: '#80cbc4',
    ECB: '#64b5f6',
    BOE: '#ffd54f',
    SNB: '#ffb74d',
    RBA: '#aed581',
    BOC: '#ba68c8',
    BOJ: '#f06292'
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const formatDate = (val) => {
    if (!val) return 'N/A';
    let year, month, day;
    if (/^\d{4}-\d{2}-\d{2}/.test(val)) {
        const parts = val.split('T')[0].split('-').map(Number);
        year = parts[0];
        month = parts[1] - 1;
        day = parts[2];
    } else {
        const parsed = new Date(val);
        if (!isNaN(parsed.getTime())) {
            year = parsed.getFullYear();
            month = parsed.getMonth();
            day = parsed.getDate();
        }
    }

    if (year === undefined) return val;

    const monthStr = MONTHS[month] || '';
    const currentYear = new Date().getFullYear();

    if (year === currentYear || year === 2026) {
        return `${monthStr} ${day}`;
    }
    return `${monthStr} ${day}, ${year}`;
};

const parseLastChangeParts = (str, cb) => {
    if (!str) return { dateStr: 'N/A', bpStr: null, isCut: false, isHike: false };

    const match = str.match(/^([A-Za-z]{3}\s+\d{1,2}(?:,\s+\d{4})?)\s*(?:\(([^)]+)\))?$/);
    let datePart = str;
    let bpPart = null;

    if (match) {
        datePart = match[1];
        bpPart = match[2];
    }

    const formattedDate = formatDate(datePart);
    let isCut = cb?.direction === 'cut' || (cb?.bpsChange !== undefined && cb.bpsChange < 0);
    let isHike = cb?.direction === 'hike' || (cb?.bpsChange !== undefined && cb.bpsChange > 0);
    let displayBp = null;

    if (bpPart) {
        const numMatch = bpPart.match(/(-?\+?\d+)\s*(?:bp|pts)?/i);
        if (numMatch) {
            const num = parseInt(numMatch[1], 10);
            if (num < 0 || isCut) {
                displayBp = `-${Math.abs(num)}bp`;
                isCut = true;
                isHike = false;
            } else if (num > 0 || isHike) {
                displayBp = `+${Math.abs(num)}bp`;
                isHike = true;
                isCut = false;
            } else {
                displayBp = `${num}bp`;
            }
        } else {
            displayBp = bpPart;
        }
    } else if (cb?.bpsChange !== undefined && cb.bpsChange !== 0) {
        if (cb.bpsChange < 0) {
            displayBp = `${cb.bpsChange}bp`;
            isCut = true;
        } else {
            displayBp = `+${cb.bpsChange}bp`;
            isHike = true;
        }
    }

    return { dateStr: formattedDate, bpStr: displayBp, isCut, isHike };
};

export default function CentralBanksTab({ data }) {
    const theme = useTheme();
    const centralBanks = useMemo(() => {
        const list = data?.centralBanks || [];
        return list.filter(cb => G10_KEYS.includes(cb.key));
    }, [data?.centralBanks]);
    const historicalRates = useMemo(() => data?.historicalRates || [], [data?.historicalRates]);

    const [chartType, setChartType] = useState('histogram'); // 'histogram' | 'timeline'
    const [timeframe, setTimeframe] = useState('5Y');
    const [selectedBanks, setSelectedBanks] = useState(G10_KEYS);

    const toggleBank = (key) => {
        setSelectedBanks(prev => {
            if (prev.includes(key)) {
                if (prev.length <= 1) return prev;
                return prev.filter(k => k !== key);
            } else {
                return [...prev, key];
            }
        });
    };

    const selectAllBanks = () => {
        setSelectedBanks(centralBanks.map(cb => cb.key));
    };

    const selectG10Only = () => {
        setSelectedBanks(G10_KEYS);
    };

    const filteredTimeline = useMemo(() => {
        if (!historicalRates.length) return [];
        if (timeframe === 'MAX') return historicalRates;
        if (timeframe === '1Y') return historicalRates.slice(-3);
        if (timeframe === '2Y') return historicalRates.slice(-5);
        if (timeframe === '5Y') return historicalRates.slice(-10);
        return historicalRates;
    }, [historicalRates, timeframe]);

    const histogramData = useMemo(() => {
        return [...centralBanks]
            .filter(cb => selectedBanks.includes(cb.key))
            .sort((a, b) => b.currentRate - a.currentRate)
            .map(cb => ({
                key: cb.key,
                name: cb.key,
                fullName: cb.name,
                country: cb.country,
                rate: cb.currentRate
            }));
    }, [centralBanks, selectedBanks]);

    const upcomingMeetings = useMemo(() => {
        return [...centralBanks]
            .filter(cb => cb.daysUntilMeeting !== undefined && cb.daysUntilMeeting >= 0)
            .sort((a, b) => a.daysUntilMeeting - b.daysUntilMeeting)
            .slice(0, 4);
    }, [centralBanks]);

    return (
        <Container>
            <ScrollableContent>
                <Stack>
                    {/* Next Meetings Row */}
                    <div>
                        <div style={{ marginBottom: '8px' }}>
                            <SectionTitle>UPCOMING RATE DECISIONS</SectionTitle>
                        </div>
                        <MeetingsGrid>
                            {upcomingMeetings.map((cb, idx) => (
                                <MeetingCard key={cb.key}>
                                    <MeetingHeader>
                                        <MeetingBank>{cb.key}</MeetingBank>
                                        <MeetingBadge $urgent={cb.daysUntilMeeting <= 7}>
                                            {cb.daysUntilMeeting === 0 ? 'TODAY' : `IN ${cb.daysUntilMeeting}D`}
                                        </MeetingBadge>
                                    </MeetingHeader>
                                    <MeetingDate>{formatDate(cb.nextMeetingIso || cb.nextMeeting)}</MeetingDate>
                                    <MeetingSub>
                                        Rate: <strong style={{ color: '#80cbc4' }}>{cb.currentRate.toFixed(2)}%</strong> • {cb.stance}
                                    </MeetingSub>
                                </MeetingCard>
                            ))}
                        </MeetingsGrid>
                    </div>

                    {/* Chart Card */}
                    <Card>
                        <CardHeader>
                            <SectionTitle>POLICY RATES COMPARISON</SectionTitle>
                            <ControlsGroup>
                                <div style={{ display: 'flex', gap: '3px' }}>
                                    <ToggleBtn
                                        $active={chartType === 'histogram'}
                                        onClick={() => setChartType('histogram')}
                                    >
                                        Current Rates
                                    </ToggleBtn>
                                    <ToggleBtn
                                        $active={chartType === 'timeline'}
                                        onClick={() => setChartType('timeline')}
                                    >
                                        Timeline
                                    </ToggleBtn>
                                </div>

                                {chartType === 'timeline' && (
                                    <div style={{ display: 'flex', gap: '3px', marginLeft: '6px' }}>
                                        {['1Y', '2Y', '5Y', 'MAX'].map(tf => (
                                            <ToggleBtn
                                                key={tf}
                                                $active={timeframe === tf}
                                                onClick={() => setTimeframe(tf)}
                                            >
                                                {tf}
                                            </ToggleBtn>
                                        ))}
                                    </div>
                                )}

                                <div style={{ display: 'flex', gap: '3px', marginLeft: '6px' }}>
                                    <ToggleBtn onClick={selectG10Only}>G10</ToggleBtn>
                                    <ToggleBtn onClick={selectAllBanks}>All</ToggleBtn>
                                </div>
                            </ControlsGroup>
                        </CardHeader>

                        {/* Chips */}
                        <ChipsWrap>
                            {centralBanks.map(cb => {
                                const isSelected = selectedBanks.includes(cb.key);
                                return (
                                    <BankChip
                                        key={cb.key}
                                        $selected={isSelected}
                                        onClick={() => toggleBank(cb.key)}
                                    >
                                        {cb.key}
                                    </BankChip>
                                );
                            })}
                        </ChipsWrap>

                        {/* Chart */}
                        <div style={{ width: '100%', height: '240px', marginTop: '6px' }}>
                            <ResponsiveContainer width="100%" height="100%">
                                {chartType === 'histogram' ? (
                                    <BarChart
                                        data={histogramData}
                                        margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                                    >
                                        <XAxis
                                            dataKey="name"
                                            stroke="rgba(255, 255, 255, 0.15)"
                                            tick={{ fill: tokens(theme.palette.mode).grey[300], fontSize: 10, fontWeight: 600 }}
                                            tickLine={false}
                                        />
                                        <YAxis
                                            stroke="rgba(255, 255, 255, 0.15)"
                                            tick={{ fill: tokens(theme.palette.mode).grey[400], fontSize: 10 }}
                                            tickLine={false}
                                            unit="%"
                                        />
                                        <ReferenceLine y={2.0} stroke="rgba(255, 255, 255, 0.2)" strokeDasharray="3 3" />
                                        <Tooltip
                                            cursor={{ fill: 'rgba(128, 203, 196, 0.08)' }}
                                            content={({ active, payload }) => {
                                                if (active && payload && payload.length) {
                                                    const d = payload[0].payload;
                                                    return (
                                                        <div style={{
                                                            background: theme.palette.mode === 'dark' ? '#0d1117' : '#fff',
                                                            border: '1px solid rgba(128, 203, 196, 0.3)',
                                                            borderRadius: '4px',
                                                            padding: '6px 10px',
                                                            fontSize: '11px'
                                                        }}>
                                                            <div style={{ fontWeight: 700 }}>{d.fullName} ({d.name})</div>
                                                            <div style={{ color: tokens(theme.palette.mode).grey[400] }}>{d.country}</div>
                                                            <div style={{ fontWeight: 700, color: '#80cbc4', marginTop: 3 }}>
                                                                Rate: {d.rate.toFixed(2)}%
                                                            </div>
                                                        </div>
                                                    );
                                                }
                                                return null;
                                            }}
                                        />
                                        <Bar dataKey="rate" fill="#80cbc4" radius={[3, 3, 0, 0]} />
                                    </BarChart>
                                ) : (
                                    <LineChart
                                        data={filteredTimeline}
                                        margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                                    >
                                        <XAxis
                                            dataKey="date"
                                            stroke="rgba(255, 255, 255, 0.15)"
                                            tick={{ fill: tokens(theme.palette.mode).grey[400], fontSize: 10 }}
                                            tickLine={false}
                                        />
                                        <YAxis
                                            stroke="rgba(255, 255, 255, 0.15)"
                                            tick={{ fill: tokens(theme.palette.mode).grey[400], fontSize: 10 }}
                                            tickLine={false}
                                            unit="%"
                                        />
                                        <ReferenceLine y={0} stroke="rgba(255, 255, 255, 0.1)" />
                                        <Tooltip
                                            cursor={{ stroke: 'rgba(255, 255, 255, 0.15)', strokeDasharray: '3 3' }}
                                            content={({ active, payload, label }) => {
                                                if (active && payload && payload.length) {
                                                    return (
                                                        <div style={{
                                                            background: theme.palette.mode === 'dark' ? '#0d1117' : '#fff',
                                                            border: '1px solid rgba(128, 203, 196, 0.3)',
                                                            borderRadius: '4px',
                                                            padding: '6px 10px',
                                                            fontSize: '11px'
                                                        }}>
                                                            <div style={{ fontWeight: 700, color: tokens(theme.palette.mode).grey[300] }}>{label}</div>
                                                            {payload.map(p => (
                                                                <div key={p.dataKey} style={{ display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
                                                                    <span style={{ color: p.color }}>{p.dataKey}:</span>
                                                                    <strong>{Number(p.value).toFixed(2)}%</strong>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    );
                                                }
                                                return null;
                                            }}
                                        />
                                        <Legend wrapperStyle={{ fontSize: '10px', paddingTop: '6px' }} />
                                        {selectedBanks.map(bankKey => (
                                            <Line
                                                key={bankKey}
                                                type="monotone"
                                                dataKey={bankKey}
                                                stroke={TIMELINE_COLORS[bankKey] || '#80cbc4'}
                                                strokeWidth={1.8}
                                                dot={false}
                                                isAnimationActive={false}
                                            />
                                        ))}
                                    </LineChart>
                                )}
                            </ResponsiveContainer>
                        </div>
                    </Card>

                    {/* Central Banks Table */}
                    <Card>
                        <CardHeader>
                            <SectionTitle>Overview</SectionTitle>
                        </CardHeader>

                        <TableWrapper>
                            <Table>
                                <thead>
                                    <tr>
                                        <Th>CENTRAL BANK</Th>
                                        <Th $align="center">CURRENCY</Th>
                                        <Th $align="center">CURRENT RATE</Th>
                                        <Th $align="center">NEXT MEETING</Th>
                                        <Th>LAST CHANGE</Th>
                                        <Th>STANCE</Th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {centralBanks.map(cb => {
                                        return (
                                            <Tr key={cb.key}>
                                                <Td>
                                                    <span style={{ fontWeight: 700, marginRight: 6 }}>{cb.key}</span>
                                                    <span style={{ color: tokens(theme.palette.mode).grey[400] }}>- {cb.name}</span>
                                                </Td>
                                                <Td $align="center">
                                                    <span style={{
                                                        padding: '1px 5px',
                                                        borderRadius: '3px',
                                                        background: 'rgba(128, 203, 196, 0.12)',
                                                        color: '#80cbc4',
                                                        fontWeight: 700,
                                                        fontSize: '10.5px'
                                                    }}>
                                                        {cb.currency}
                                                    </span>
                                                </Td>
                                                <Td $align="center" style={{ fontSize: '13px', fontWeight: 700, color: '#80cbc4' }}>
                                                    {cb.currentRate.toFixed(2)}%
                                                </Td>
                                                <Td $align="center">
                                                    {formatDate(cb.nextMeetingIso || cb.nextMeeting)}
                                                </Td>
                                                {(() => {
                                                    const lastChangeInfo = parseLastChangeParts(cb.lastChange, cb);
                                                    return (
                                                        <Td>
                                                            <div style={{ display: 'inline-flex', alignItems: 'center' }}>
                                                                <span>{lastChangeInfo.dateStr}</span>
                                                                {lastChangeInfo.bpStr && (
                                                                    <BpBadge $isCut={lastChangeInfo.isCut} $isHike={lastChangeInfo.isHike}>
                                                                        {lastChangeInfo.bpStr}
                                                                    </BpBadge>
                                                                )}
                                                            </div>
                                                        </Td>
                                                    );
                                                })()}
                                                <Td>
                                                    <StanceBadge>
                                                        {cb.stance}
                                                    </StanceBadge>
                                                </Td>
                                            </Tr>
                                        );
                                    })}
                                </tbody>
                            </Table>
                        </TableWrapper>
                    </Card>
                </Stack>
            </ScrollableContent>
        </Container>
    );
}
